import { Component, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ListaTipoSituacaoTributariaICMSRegimeNormal, ListaTipoSituacaoTributariaICMSSimples, TipoSituacaoTributariaCOFINS, TipoSituacaoTributariaIPI, TipoSituacaoTributariaPIS } from '@models';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { NFEntradaService } from '../../../services/nfentrada.service';
import { ModalCadastroComponent } from '../../../shared/modal-cadastro/modal-cadastro.component';
import { getErrorMessage, markFormGroupTouched } from '@utils';
import { MessageService } from '@services';
import { CustomValidators } from '../../../general/validations/custom-validators';
import * as moment from 'moment';
import { ClassificacoesFiscaisService } from '../../../services/classificacoes-fiscais.service';
import { ContasContabeisService } from '../../../services/contas-contabeis.service';

@Component({
  selector: 'app-modal-escriturar-nota-entrada',
  templateUrl: './modal-escriturar-nota-entrada.component.html',
  styleUrls: ['./modal-escriturar-nota-entrada.component.css']
})
export class ModalEscriturarNotaEntradaComponent extends ModalCadastroComponent implements OnInit {

  @ViewChild('modal', { static: true }) modal: any;

  form = new FormGroup({
    nfeId: new FormControl(null),
    dataEntrada: new FormControl(null, [Validators.required, CustomValidators.dataMenorOuIgualAtual(), this.dataEntradaMaiorOuIgualEmissao()]),
    itens: new FormArray([]),
    atualizarCustoUnitario: new FormControl(true),
  });
  dados: any;
  cstIcms = [];
  cstIpi = new TipoSituacaoTributariaIPI().ListarEntrada();
  cstPis = new TipoSituacaoTributariaPIS().ListarEntrada();
  cstCofins = new TipoSituacaoTributariaCOFINS().ListarEntrada();
  cfops = [];
  contasContabeis = [];

  aplicarATodos = {
    cstIcms: null,
    aliquotaIcms: null,
    cstIpi: null,
    aliquotaIpi: null,
    cstPis: null,
    aliquotaPis: null,
    cstCofins: null,
    aliquotaCofins: null,
    cfop: null,
    contaContabilID: null,
  };

  constructor(
    private nfEntradaService: NFEntradaService,
    private messageService: MessageService,
    private classificacoesService: ClassificacoesFiscaisService,
    private contasContabeisService: ContasContabeisService,
  ) {
    super();
  }

  ngOnInit() {
    this.contasContabeisService.getAll().subscribe(res => {
      this.contasContabeis = res.filter(x => x.aceitaLancamento)
        .map(x => {
          return {
            ...x,
            descricao: x.contaContabil + ' - ' + x.nomeConta
          };
        });
    });
  }

  handleOnClose(): void {
    this.closed.emit();
  }

  handleOnCancel(): void {
    this.hide();
  }

  async handleOnSave(): Promise<void> {
    this.messageService.clearAll();
    markFormGroupTouched(this.form);
    if (!this.form.valid) {
      this.messageService.error('Preencha os campos destacados.', 5000);
      return;
    }

    try {
      this.loading['escriturar'] = true;

      this.messageService.addToast({
        title: 'Gerando NF-e de Entrada',
        msg: 'Aguarde um instante!',
        showClose: true, timeout: 0, theme: 'default', closeOther: true, type: 'wait'
      });

      const dados = this.form.getRawValue();
      await firstValueFrom(this.nfEntradaService.escriturarNotaEntrada(dados));

      this.messageService.success('Nota escriturada com sucesso.', 5000);
      this.closed.emit({ sucesso: true });
    } catch(err) {
      console.error(err);
      this.messageService.error(getErrorMessage(err), 8000);
    } finally {
      this.loading['escriturar'] = false;
    }
  }

async carregaDados() {
  try {
    this.messageService.wait('Carregando nota fiscal, aguarde...', 0);

    const nfeId = this.form.get('nfeId').value;
    this.dados = await lastValueFrom(this.nfEntradaService.buscarNfeParaEscrituracao(nfeId));
    this.form.get('dataEntrada').setValue(this.dados.dataEntrada);

    this.cstIcms = [1, 4].includes(this.dados.unidadeEmpresaCrt)
      ? ListaTipoSituacaoTributariaICMSSimples
      : ListaTipoSituacaoTributariaICMSRegimeNormal;

    this.cfops = this.dados.cfops;

    for (const item of this.dados.itens) {
      const res = await lastValueFrom(this.classificacoesService.GetNCMDetalhado(item.ncm));

      const group = new FormGroup({
        // Somente leitura
        ncm: new FormControl(res?.classificacaoFiscal || item.ncm),
        $cst: new FormControl(res?.cst || item.cst),
        $aliquotaIcms: new FormControl(res?.aliquotaICMS ?? (item.aliquotaIcms || 0)),
        $cstIpi: new FormControl(res?.situacaoTributariaIPI || item.cstIpi),
        $aliquotaIpi: new FormControl(res?.aliquotaIPI ?? (item.aliquotaIpi || 0)),
        $cstPis: new FormControl(res?.situacaoTributariaPIS || item.cstPis),
        $aliquotaPis: new FormControl(res?.aliquotaPIS ?? (item.aliquotaPis || 0)),
        $cstCofins: new FormControl(res?.situacaoTributariaCOFINS || item.cstCofins),
        $aliquotaCofins: new FormControl(res?.aliquotaCOFINS ?? (item.aliquotaCofins || 0)),
        $cfop: new FormControl(item.cfop),
        $descricao: new FormControl(item.descricao),

        // Editáveis
        cst: new FormControl(res?.cst || null, [Validators.required]),
        aliquotaIcms: new FormControl(res?.aliquotaICMS ?? (item.aliquotaIcms || 0), [Validators.required]),
        cstIpi: new FormControl(res?.situacaoTributariaIPI || null, [Validators.required]),
        aliquotaIpi: new FormControl(res?.aliquotaIPI ?? (item.aliquotaIpi || 0), [Validators.required]),
        cstPis: new FormControl(res?.situacaoTributariaPIS || null, [Validators.required]),
        aliquotaPis: new FormControl(res?.aliquotaPIS ?? (item.aliquotaPis || 0), [Validators.required]),
        cstCofins: new FormControl(res?.situacaoTributariaCOFINS || null, [Validators.required]),
        aliquotaCofins: new FormControl(res?.aliquotaCOFINS ?? (item.aliquotaCofins || 0), [Validators.required]),
        cfop: new FormControl(res?.codigoFiscalId ?? (item.cfop || null), [Validators.required]),
        contaContabilID: new FormControl((item.contaContabilID || null)),
      });

      (this.form.get('itens') as FormArray).push(group);
    }

    this.messageService.clearAll();
    return true;

  } catch (error) {
    console.error(error);
    this.messageService.error(getErrorMessage(error), 8000);
    this.closed.emit();
    return false;
  }
}

  async show() {
    this.form.get('nfeId').setValue(this.prop.nfeId);
    const sucesso = await this.carregaDados();
    if (sucesso) {
      this.modal.show();
    }
  }

  private hide(): void {
    this.modal.hide();
  }

  get formItens() {
    return this.form.get('itens') as FormArray;
  }

  aplicarTodos() {
    for (const item of this.formItens.controls) {
      if (this.aplicarATodos.cstIcms) item.get('cst').setValue(this.aplicarATodos.cstIcms);
      if (this.aplicarATodos.aliquotaIcms) item.get('aliquotaIcms').setValue(this.aplicarATodos.aliquotaIcms);
      if (this.aplicarATodos.cstIpi) item.get('cstIpi').setValue(this.aplicarATodos.cstIpi);
      if (this.aplicarATodos.aliquotaIpi) item.get('aliquotaIpi').setValue(this.aplicarATodos.aliquotaIpi);
      if (this.aplicarATodos.cstPis) item.get('cstPis').setValue(this.aplicarATodos.cstPis);
      if (this.aplicarATodos.aliquotaPis) item.get('aliquotaPis').setValue(this.aplicarATodos.aliquotaPis);
      if (this.aplicarATodos.cstCofins) item.get('cstCofins').setValue(this.aplicarATodos.cstCofins);
      if (this.aplicarATodos.aliquotaCofins) item.get('aliquotaCofins').setValue(this.aplicarATodos.aliquotaCofins);
      if (this.aplicarATodos.cfop) item.get('cfop').setValue(this.aplicarATodos.cfop);
      item.get('contaContabilID').setValue(this.aplicarATodos.contaContabilID);
    }

    this.messageService.success('Configuração aplicada a todos os NCMs.', 5000);
  }

  dataEntradaMaiorOuIgualEmissao(): ValidatorFn {
    return (control: FormControl) => {
      if (!control.value || !this.dados)
        return null;

      const dataEmissao = moment(this.dados.emissao).startOf('day');
      const dataEntrada = moment(control.value).startOf('day');

      return dataEntrada.isSameOrAfter(dataEmissao)
        ? null
        : { custom: { message: "Data deve ser maior ou igual a data de Emissão" } };
    }
  }
}
